import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Autocomplete,
  Chip,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import ArticleIcon from '@mui/icons-material/Article';
import { Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend 
} from 'chart.js';
import Layout from '../components/layout/LegalLayout';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

const ResearchPage = () => {
  // State for search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState(null);
  const [researchResults, setResearchResults] = useState([]);

  // Sample research categories
  const researchCategories = [
    'Tax Law', 
    'Corporate Compliance', 
    'Accounting Regulations', 
    'Financial Fraud', 
    'International Taxation'
  ];

  // Sample research results
  const sampleResults = [
    {
      id: 'RES-001',
      title: 'Recent Changes in Corporate Tax Legislation',
      type: 'Article',
      date: '2024-05-15',
      source: 'Legal Insights Journal',
      relevanceScore: 85
    },
    {
      id: 'RES-002',
      title: 'Impact of AI on Financial Compliance',
      type: 'Research Paper',
      date: '2024-06-01',
      source: 'Global Accounting Review',
      relevanceScore: 92
    },
    {
      id: 'RES-003',
      title: 'International Tax Harmonization Trends',
      type: 'Whitepaper',
      date: '2024-04-22',
      source: 'World Economic Forum',
      relevanceScore: 78
    }
  ];

  // Research sources distribution chart data
  const sourcesChartData = {
    labels: ['Journals', 'Academic Papers', 'Whitepapers', 'Legal Databases'],
    datasets: [{
      data: [35, 25, 20, 20],
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 206, 86, 0.6)',
        'rgba(75, 192, 192, 0.6)'
      ]
    }]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      title: {
        display: true,
        text: 'Research Sources Distribution'
      }
    }
  };

  // Search handler
  const handleSearch = () => {
    // Simulate search - in real app, this would be an API call
    const filteredResults = sampleResults.filter(result => 
      result.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (searchCategory && result.type.toLowerCase().includes(searchCategory.toLowerCase()))
    );
    setResearchResults(filteredResults);
  };

  return (
    <Layout>
          <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <LocalLibraryIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Research Portal
      </Typography>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Search Research Materials"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 2 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={researchCategories}
                renderInput={(params) => 
                  <TextField 
                    {...params} 
                    label="Select Category" 
                    variant="outlined" 
                  />
                }
                value={searchCategory}
                onChange={(_, newValue) => setSearchCategory(newValue)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                fullWidth 
                variant="contained" 
                color="primary" 
                onClick={handleSearch}
              >
                Search Research
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Research Sources Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Pie data={sourcesChartData} options={chartOptions} />
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Research Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Research Overview</Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1">Total Materials</Typography>
                  <Typography variant="h5">150</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle1">New This Month</Typography>
                  <Typography variant="h5" color="primary">22</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle1">Categories</Typography>
                  <Typography variant="h5">5</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Search Results */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ArticleIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                Research Results
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {researchResults.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No results found. Try a different search query.
                </Typography>
              ) : (
                researchResults.map((result) => (
                  <Card key={result.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={8}>
                          <Typography variant="h6">{result.title}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {result.source} | {result.date}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Chip 
                            label={result.type} 
                            color="primary" 
                            size="small" 
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Chip 
                            label={`Relevance: ${result.relevanceScore}%`} 
                            color="secondary" 
                            size="small" 
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>

    </Layout>
  
  );
};

export default ResearchPage;